import {html, PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html as litHtml} from 'lit';
import {until} from 'lit/directives/until.js';
import "@cbar/cbar-autocomplete/cbar-autocomplete.js";
import "@cbar/cbar-img-cache/cbar-img-cache";
import CbarImgCache from "@cbar/cbar-img-cache/cbar-img-cache";

/**
 * `cbar-entity-autocomplete`
 * Show autocompletion for a list of entities based on a search
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class CbarEntityAutocomplete extends PolymerElement {
  static get template() {
    return html`
      <style>
        :host {
          display: block;
        }

        cbar-autocomplete {
          width: 100%;
        }
      </style>

      <cbar-autocomplete
          dense$="[[dense]]"
          datasource="[[datasource]]"
          render-suggestion="[[renderSuggestion]]"
          search-fields="[[searchFields]]"
          required$="[[required]]"
          label="[[label]]"
          placeholder="[[placeholder]]"
          minLength="1"
          value="[[value]]"
          fixedMenuPosition$="[[fixedMenuPosition]]"
          hideSearch$="[[hideSearch]]"
          on-value-changed="onValueChanged"
          id="dropdown"
          validation-message="[[validationMessage]]"
      ></cbar-autocomplete>
    `;
  }

  static get properties() {
    return {
      /**
       * An array of entity objects, each containing a title,
       * description, imageUri & [[textProperty]]
       */
      source: {
        type: Array,
        observer: 'sourceChanged',
      },

      /**
       * An array of fields to search on when performing autocompletion
       */
      searchFields: {
        type: Array,
        value: [
          'title',
          'description',
        ]
      },

      /**
       * The property of the source items to display when selected
       */
      textProperty: {
        type: String,
        value: 'text',
      },

      /**
       * Property of local datasource to as the value property
       */
      valueProperty: {
        type: String,
        value: 'value'
      },

      /**
       * The title property to display in the suggestions above the description
       */
      titleProperty: {
        type: String,
        value: 'displayText',
      },

      /**
       * `label` Text to display as the input label
       */
      label: String,

      /**
       * The placeholder text
       */
      placeholder: String,

      /**
       * `value` Selected object from the suggestions
       */
      value: {
        type: String,
        notify: true,
      },

      /**
       * Material dense text input (smaller)
       */
      dense: {
        type: Boolean,
        value: false,
      },

      /**
       * stops the image caching from occurring
       */
      noCache: {
        type: Boolean,
        value: false
      },

      /**
       * Proxies to mwc-menu's fixed attribute.
       *
       * Makes the menu float; useful for dialogs
       */
      fixedMenuPosition: {
        type: Boolean,
        value: false,
      },
      required: {
        type: Boolean,
        value: false
      },
      validationMessage: {
        type: String,
        value: ""
      },
      hideSearch: {
        type: Boolean,
        value: false
      }
    };
  }

  ready() {
    super.ready();

    this._imageCache = new CbarImgCache();

    this.renderSuggestion = this.renderSuggestion.bind(this);
  }

  sourceChanged(source) {
    this.datasource = source.map(item => ({
      ...item,
      text: item[ this.textProperty ],
      value: item[ this.valueProperty ],
    }));
  }

  onValueChanged(e) {
    this.value = e.detail.value;
  }

  renderSuggestion(suggestion) {
    let image = suggestion.imageUri;
    if (!this.noCache && image) {
      image = until(this.getCachedImage(suggestion));
    }

    return litHtml`
        <style>
          .account-item {
            padding: 8px 16px;
          }

          .item-container {
            display: flex;
            align-items: center;
          }

          .name-description-container {
            display: flex;
            flex-direction: column;
            justify-content: center;

            margin-left: 15px;

            overflow: hidden;
          }

          .image {
            -webkit-border-radius: 50%;
            -moz-border-radius: 50%;
            border-radius: 50%;

            height: 40px;
          }

          .description {
            margin-top: 4px;
            color: #999;
            font-size: small;

            overflow: hidden;
            text-overflow: ellipsis;
          }
        </style>

        <mwc-list-item class="account-item" role="option" aria-selected="false">
            <div class="item-container">
                ${image ? litHtml`<img class="image" src="${image}" alt="Profile image" />` : null}

                <div class="name-description-container">
                    <div class="full-name">${suggestion[ this.titleProperty ]}</div>
                    <div class="description">${suggestion.description}</div>
                </div>
            </div>
        </mwc-list-item>
      `;
  }

  getCachedImage(item) {
    if (!item.imageUri) {
      return null;
    }

    return this._imageCache.getBase64(item.imageUri);
  }

  validate(){
    return this.$.dropdown.reportValidity();
  }
}

window.customElements.define('cbar-entity-autocomplete', CbarEntityAutocomplete);
